// components/SidCheck.js - Complete Three-Column Layout Implementation
import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Search, MapPin, Filter, AlertCircle, Download } from 'lucide-react';
import { styles } from '../styles';
import { analyzeOriginsByCountry } from '../services/api/analysisApi';

const SidCheck = ({
                      searchTerm,
                      originFilter,
                      countryFilter,
                      selectedTable,
                      countries
                  }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [originData, setOriginData] = useState([]);
    const [analysisInfo, setAnalysisInfo] = useState(null);
    const [selectedDestination, setSelectedDestination] = useState('');

    // Fetch origin analysis when destination filter changes
    const fetchOriginAnalysis = useCallback(async () => {
        if (!selectedDestination || selectedDestination === 'All Countries') {
            setError('Please select a specific destination country to analyze origins.');
            setOriginData([]);
            setAnalysisInfo(null);
            return;
        }

        setLoading(true);
        setError('');

        try {
            console.log('🔍 Starting Sid Check origin analysis for destination:', selectedDestination);

            const response = await analyzeOriginsByCountry({
                destination: selectedDestination,
                search: searchTerm || '',
                origin: originFilter || '',
                table: selectedTable || 'access_entries'
            });

            if (response.success) {
                setOriginData(response.origins || []);
                setAnalysisInfo(response);
                console.log(`✅ Sid Check analysis completed: ${response.unique_origins} origins found`);
            } else {
                setError(response.error || 'Analysis failed');
                setOriginData([]);
                setAnalysisInfo(null);
            }
        } catch (err) {
            console.error('❌ Sid Check analysis error:', err);
            setError('Failed to analyze origins: ' + err.message);
            setOriginData([]);
            setAnalysisInfo(null);
        } finally {
            setLoading(false);
        }
    }, [selectedDestination, searchTerm, originFilter, selectedTable]);

    // Auto-run analysis when destination changes
    useEffect(() => {
        if (selectedDestination && selectedDestination !== 'All Countries') {
            const timeoutId = setTimeout(() => {
                fetchOriginAnalysis();
            }, 500); // Debounce for 500ms
            return () => clearTimeout(timeoutId);
        } else {
            setOriginData([]);
            setAnalysisInfo(null);
        }
    }, [selectedDestination, fetchOriginAnalysis]);

    // Initialize with country filter if available
    useEffect(() => {
        if (countryFilter && countryFilter !== 'All Countries' && !selectedDestination) {
            setSelectedDestination(countryFilter);
        }
    }, [countryFilter, selectedDestination]);

    // Export origins to CSV
    const exportOrigins = () => {
        if (!originData || originData.length === 0) {
            alert('No origin data to export');
            return;
        }

        const csvData = originData.map((origin, index) => ({
            rank: index + 1,
            origin: origin.origin,
            count: origin.count,
            percentage: analysisInfo?.total_records
                ? ((origin.count / analysisInfo.total_records) * 100).toFixed(2) + '%'
                : '0.00%'
        }));

        const csvContent = [
            ['Rank', 'Origin', 'Count', 'Percentage'],
            ...csvData.map(row => [row.rank, row.origin, row.count, row.percentage])
        ].map(row => row.join(',')).join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `sid_check_origins_${selectedDestination}_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    return (
        <div style={styles.tableContainer}>
            <div style={styles.tableHeader}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <h2 style={{
                        ...styles.tableTitle,
                        color: '#ffffff',
                        fontSize: '1.25rem',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center'
                    }}>
                        <Search size={24} style={{ marginRight: '8px', color: '#a855f7' }} />
                        Sid Check - Origin Analysis
                    </h2>

                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                        <button
                            onClick={fetchOriginAnalysis}
                            disabled={loading || !selectedDestination || selectedDestination === 'All Countries'}
                            style={{
                                ...styles.button,
                                ...(loading ? styles.buttonDisabled : styles.buttonPrimary)
                            }}
                        >
                            <RefreshCw size={16} style={loading ? { animation: 'spin 1s linear infinite' } : {}} />
                            {loading ? 'Analyzing...' : 'Refresh Analysis'}
                        </button>

                        <button
                            onClick={exportOrigins}
                            disabled={!originData || originData.length === 0}
                            style={{
                                ...styles.button,
                                ...styles.buttonSecondary,
                                opacity: (!originData || originData.length === 0) ? 0.6 : 1
                            }}
                        >
                            <Download size={16} />
                            Export CSV
                        </button>
                    </div>
                </div>

                {/* Destination Selector */}
                <div style={{
                    marginBottom: '20px',
                    padding: '16px',
                    backgroundColor: '#1f2937',
                    borderRadius: '8px',
                    border: '1px solid #374151'
                }}>
                    <label style={{
                        display: 'block',
                        marginBottom: '8px',
                        color: '#d1d5db',
                        fontSize: '0.9rem',
                        fontWeight: '500'
                    }}>
                        <MapPin size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                        Select Destination Country to Analyze:
                    </label>
                    <select
                        value={selectedDestination}
                        onChange={(e) => setSelectedDestination(e.target.value)}
                        style={{
                            ...styles.select,
                            width: '100%',
                            maxWidth: '400px',
                            // 🎨 Enhanced Dark Theme Styling
                            backgroundColor: '#374151',
                            color: '#ffffff',
                            border: '1px solid #6b7280',
                            padding: '12px 16px',
                            borderRadius: '6px',
                            fontSize: '0.9rem',
                            fontWeight: '500',
                            cursor: 'pointer'
                        }}
                    >
                        <option value="">-- Select a Destination --</option>
                        <option value="All Countries" disabled>All Countries (Not supported)</option>
                        {countries
                            .slice() // Create a copy to avoid mutating the original array
                            .sort((a, b) => a.localeCompare(b)) // Sort alphabetically A-Z
                            .map((country, index) => (
                                <option key={index} value={country}>
                                    {country}
                                </option>
                            ))}
                    </select>
                </div>

                {/* Error Message */}
                {error && (
                    <div style={{
                        ...styles.errorAlert,
                        marginBottom: '20px'
                    }}>
                        <AlertCircle size={20} />
                        {error}
                    </div>
                )}

                {/* Analysis Info */}
                {analysisInfo && !loading && (
                    <div style={{
                        marginBottom: '20px',
                        padding: '16px',
                        backgroundColor: '#065f46',
                        borderRadius: '8px',
                        border: '1px solid #10b981'
                    }}>
                        <div style={{ color: '#d1fae5', fontSize: '0.95rem' }}>
                            <strong>📊 Analysis Results for "{selectedDestination}":</strong>
                            <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
                                <span>📈 Total Records: {analysisInfo.total_records?.toLocaleString() || 0}</span>
                                <span>🌍 Unique Origins: {analysisInfo.unique_origins?.toLocaleString() || 0}</span>
                                {analysisInfo.search_info?.search_text && (
                                    <span>🔍 Search: "{analysisInfo.search_info.search_text}"</span>
                                )}
                                {analysisInfo.search_info?.origin_filter && (
                                    <span>📍 Origin Filter: "{analysisInfo.search_info.origin_filter}"</span>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Loading State */}
                {loading && (
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: '60px',
                        color: '#d1d5db'
                    }}>
                        <RefreshCw size={32} style={{ color: '#a855f7', animation: 'spin 1s linear infinite', marginRight: '12px' }} />
                        <span>Analyzing origins for {selectedDestination}...</span>
                    </div>
                )}

                {/* THREE COLUMN LAYOUT - WITH UPDATED COLOR SCHEME */}
                {!loading && originData && originData.length > 0 && (
                    <div style={{
                        width: '100%',
                        marginTop: '20px'
                    }}>
                        {/* Three Column Container */}
                        <div style={{
                            width: '100%',
                            display: 'flex',
                            gap: '15px'
                        }}>
                            {/* COLUMN 1 */}
                            <div style={{
                                width: '33%',
                                backgroundColor: '#1f2937',
                                borderRadius: '8px',
                                padding: '10px',
                                border: '1px solid #374151'
                            }}>
                                <h4 style={{
                                    color: '#ffffff',
                                    margin: '0 0 10px 0',
                                    fontSize: '0.9rem',
                                    textAlign: 'center',
                                    fontWeight: '600'
                                }}>
                                    Column 1: Ranks 1-{Math.ceil(originData.length / 3)}
                                </h4>

                                {/* Table Header */}
                                <div style={{
                                    display: 'flex',
                                    backgroundColor: '#374151',
                                    padding: '10px',
                                    borderRadius: '4px',
                                    marginBottom: '8px',
                                    fontWeight: '700',
                                    fontSize: '1rem', // Increased by 2px (from 0.9rem)
                                    color: '#ffffff'
                                }}>
                                    <div style={{ width: '35px', fontWeight: '700' }}>#</div>
                                    <div style={{ flex: '1', fontWeight: '700' }}>Origin</div>
                                    <div style={{ width: '70px', textAlign: 'right', fontWeight: '700' }}>Count</div>
                                    <div style={{ width: '50px', textAlign: 'right', fontWeight: '700' }}>%</div>
                                </div>

                                {/* Table Rows */}
                                {originData.slice(0, Math.ceil(originData.length / 3)).map((origin, index) => {
                                    const percentage = analysisInfo?.total_records
                                        ? ((origin.count / analysisInfo.total_records) * 100).toFixed(1)
                                        : '0.0';

                                    // Updated Color coding for percentages - GREEN HIGH, YELLOW MID, RED LOW, PURPLE VERY LOW
                                    const getPercentageColor = (percent) => {
                                        const p = parseFloat(percent);
                                        if (p >= 5) return '#10b981';   // Green for high percentages (5%+)
                                        if (p >= 1) return '#f59e0b';   // Yellow for medium (1-4.9%)
                                        if (p >= 0.1) return '#dc2626'; // Red for low (0.1-0.9%)
                                        return '#a855f7';               // Purple for very low (<0.1%)
                                    };

                                    // Check if this is a top 10 origin (globally across all data)
                                    const isTop10 = index < 10;

                                    return (
                                        <div key={index} style={{
                                            display: 'flex',
                                            padding: '8px 10px',
                                            backgroundColor: index % 2 === 0 ? '#374151' : '#1f2937', // No yellow background
                                            fontSize: '0.9rem', // Increased by 2px (from 0.85rem)
                                            color: '#ffffff', // White text for all
                                            borderRadius: '4px',
                                            marginBottom: '3px',
                                            alignItems: 'center'
                                        }}>
                                            <div style={{
                                                width: '35px',
                                                fontWeight: '700',
                                                fontSize: '1rem' // Increased by 2px
                                            }}>
                                                {index + 1}
                                            </div>
                                            <div style={{
                                                flex: '1',
                                                fontFamily: 'monospace',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                fontSize: '0.9rem', // Increased by 2px
                                                fontWeight: '500'
                                            }}>
                                                <span style={{
                                                    color: '#10b981',
                                                    marginRight: '6px',
                                                    fontSize: '0.85rem' // Increased by 2px
                                                }}>●</span>
                                                <span style={{
                                                    color: isTop10 ? '#fbbf24' : '#ffffff', // Yellow text for top 10 origins only
                                                    fontWeight: isTop10 ? '700' : '500' // Bold for top 10
                                                }}>
                                                    {origin.origin}
                                                </span>
                                            </div>
                                            <div style={{
                                                width: '70px',
                                                textAlign: 'right',
                                                fontWeight: isTop10 ? '700' : '700',
                                                fontSize: '0.9rem' // Increased by 2px
                                            }}>
                                                {origin.count.toLocaleString()}
                                            </div>
                                            <div style={{
                                                width: '50px',
                                                textAlign: 'right',
                                                color: getPercentageColor(percentage),
                                                fontWeight: '700',
                                                fontSize: '0.9rem' // Increased by 2px
                                            }}>
                                                {percentage}%
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* COLUMN 2 */}
                            <div style={{
                                width: '33%',
                                backgroundColor: '#1f2937',
                                borderRadius: '8px',
                                padding: '10px',
                                border: '1px solid #374151'
                            }}>
                                <h4 style={{
                                    color: '#ffffff',
                                    margin: '0 0 10px 0',
                                    fontSize: '0.9rem',
                                    textAlign: 'center',
                                    fontWeight: '600'
                                }}>
                                    Column 2: Ranks {Math.ceil(originData.length / 3) + 1}-{Math.ceil(originData.length * 2 / 3)}
                                </h4>

                                {/* Table Header */}
                                <div style={{
                                    display: 'flex',
                                    backgroundColor: '#374151',
                                    padding: '10px',
                                    borderRadius: '4px',
                                    marginBottom: '8px',
                                    fontWeight: '700',
                                    fontSize: '0.95rem', // Increased by 2px
                                    color: '#ffffff'
                                }}>
                                    <div style={{ width: '35px', fontWeight: '700' }}>#</div>
                                    <div style={{ flex: '1', fontWeight: '700' }}>Origin</div>
                                    <div style={{ width: '70px', textAlign: 'right', fontWeight: '700' }}>Count</div>
                                    <div style={{ width: '50px', textAlign: 'right', fontWeight: '700' }}>%</div>
                                </div>

                                {/* Table Rows */}
                                {originData.slice(Math.ceil(originData.length / 3), Math.ceil(originData.length * 2 / 3)).map((origin, index) => {
                                    const actualIndex = Math.ceil(originData.length / 3) + index;
                                    const percentage = analysisInfo?.total_records
                                        ? ((origin.count / analysisInfo.total_records) * 100).toFixed(1)
                                        : '0.0';

                                    // Updated Color coding for percentages
                                    const getPercentageColor = (percent) => {
                                        const p = parseFloat(percent);
                                        if (p >= 5) return '#10b981';   // Green for high percentages (5%+)
                                        if (p >= 1) return '#f59e0b';   // Yellow for medium (1-4.9%)
                                        if (p >= 0.1) return '#dc2626'; // Red for low (0.1-0.9%)
                                        return '#a855f7';               // Purple for very low (<0.1%)
                                    };

                                    // Check if this is a top 10 origin (globally)
                                    const isTop10 = actualIndex < 10;

                                    return (
                                        <div key={actualIndex} style={{
                                            display: 'flex',
                                            padding: '8px 10px',
                                            backgroundColor: index % 2 === 0 ? '#374151' : '#1f2937', // No yellow background
                                            fontSize: '0.9rem', // Increased by 2px
                                            color: '#ffffff', // White text for all
                                            borderRadius: '4px',
                                            marginBottom: '3px',
                                            alignItems: 'center'
                                        }}>
                                            <div style={{
                                                width: '35px',
                                                fontWeight: '700',
                                                fontSize: '0.95rem' // Increased by 2px
                                            }}>
                                                {actualIndex + 1}
                                            </div>
                                            <div style={{
                                                flex: '1',
                                                fontFamily: 'monospace',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                fontSize: '0.9rem', // Increased by 2px
                                                fontWeight: '500'
                                            }}>
                                                <span style={{
                                                    color: '#10b981',
                                                    marginRight: '6px',
                                                    fontSize: '0.85rem' // Increased by 2px
                                                }}>●</span>
                                                <span style={{
                                                    color: isTop10 ? '#fbbf24' : '#ffffff', // Yellow text for top 10 origins only
                                                    fontWeight: isTop10 ? '700' : '500' // Bold for top 10
                                                }}>
                                                    {origin.origin}
                                                </span>
                                            </div>
                                            <div style={{
                                                width: '70px',
                                                textAlign: 'right',
                                                fontWeight: '700',
                                                fontSize: '0.9rem' // Increased by 2px
                                            }}>
                                                {origin.count.toLocaleString()}
                                            </div>
                                            <div style={{
                                                width: '50px',
                                                textAlign: 'right',
                                                color: getPercentageColor(percentage),
                                                fontWeight: '700',
                                                fontSize: '0.9rem' // Increased by 2px
                                            }}>
                                                {percentage}%
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* COLUMN 3 */}
                            <div style={{
                                width: '33%',
                                backgroundColor: '#1f2937',
                                borderRadius: '8px',
                                padding: '10px',
                                border: '1px solid #374151'
                            }}>
                                <h4 style={{
                                    color: '#ffffff',
                                    margin: '0 0 10px 0',
                                    fontSize: '0.9rem',
                                    textAlign: 'center',
                                    fontWeight: '600'
                                }}>
                                    Column 3: Ranks {Math.ceil(originData.length * 2 / 3) + 1}-{originData.length}
                                </h4>

                                {/* Table Header */}
                                <div style={{
                                    display: 'flex',
                                    backgroundColor: '#374151',
                                    padding: '10px',
                                    borderRadius: '4px',
                                    marginBottom: '8px',
                                    fontWeight: '700',
                                    fontSize: '0.95rem', // Increased by 2px
                                    color: '#ffffff'
                                }}>
                                    <div style={{ width: '35px', fontWeight: '700' }}>#</div>
                                    <div style={{ flex: '1', fontWeight: '700' }}>Origin</div>
                                    <div style={{ width: '70px', textAlign: 'right', fontWeight: '700' }}>Count</div>
                                    <div style={{ width: '50px', textAlign: 'right', fontWeight: '700' }}>%</div>
                                </div>

                                {/* Table Rows */}
                                {originData.slice(Math.ceil(originData.length * 2 / 3)).map((origin, index) => {
                                    const actualIndex = Math.ceil(originData.length * 2 / 3) + index;
                                    const percentage = analysisInfo?.total_records
                                        ? ((origin.count / analysisInfo.total_records) * 100).toFixed(1)
                                        : '0.0';

                                    // Updated Color coding for percentages
                                    const getPercentageColor = (percent) => {
                                        const p = parseFloat(percent);
                                        if (p >= 5) return '#10b981';   // Green for high percentages (5%+)
                                        if (p >= 1) return '#f59e0b';   // Yellow for medium (1-4.9%)
                                        if (p >= 0.1) return '#dc2626'; // Red for low (0.1-0.9%)
                                        return '#a855f7';               // Purple for very low (<0.1%)
                                    };

                                    // Check if this is a top 10 origin (globally)
                                    const isTop10 = actualIndex < 10;

                                    return (
                                        <div key={actualIndex} style={{
                                            display: 'flex',
                                            padding: '8px 10px',
                                            backgroundColor: index % 2 === 0 ? '#374151' : '#1f2937', // No yellow background
                                            fontSize: '0.9rem', // Increased by 2px
                                            color: '#ffffff', // White text for all
                                            borderRadius: '4px',
                                            marginBottom: '3px',
                                            alignItems: 'center'
                                        }}>
                                            <div style={{
                                                width: '35px',
                                                fontWeight: '700',
                                                fontSize: '0.95rem' // Increased by 2px
                                            }}>
                                                {actualIndex + 1}
                                            </div>
                                            <div style={{
                                                flex: '1',
                                                fontFamily: 'monospace',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                fontSize: '0.9rem', // Increased by 2px
                                                fontWeight: '500'
                                            }}>
                                                <span style={{
                                                    color: '#10b981',
                                                    marginRight: '6px',
                                                    fontSize: '0.85rem' // Increased by 2px
                                                }}>●</span>
                                                <span style={{
                                                    color: isTop10 ? '#fbbf24' : '#ffffff', // Yellow text for top 10 origins only
                                                    fontWeight: isTop10 ? '700' : '500' // Bold for top 10
                                                }}>
                                                    {origin.origin}
                                                </span>
                                            </div>
                                            <div style={{
                                                width: '70px',
                                                textAlign: 'right',
                                                fontWeight: '700',
                                                fontSize: '0.9rem' // Increased by 2px
                                            }}>
                                                {origin.count.toLocaleString()}
                                            </div>
                                            <div style={{
                                                width: '50px',
                                                textAlign: 'right',
                                                color: getPercentageColor(percentage),
                                                fontWeight: '700',
                                                fontSize: '0.9rem' // Increased by 2px
                                            }}>
                                                {percentage}%
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}

                {/* No Data State */}
                {!loading && (!originData || originData.length === 0) && selectedDestination && selectedDestination !== 'All Countries' && (
                    <div style={{
                        textAlign: 'center',
                        padding: '60px',
                        color: '#9ca3af'
                    }}>
                        <Filter size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
                        <h3 style={{ color: '#d1d5db', marginBottom: '8px' }}>No Origins Found</h3>
                        <p>No access origins found for the selected destination "{selectedDestination}".</p>
                        <p style={{ fontSize: '0.9rem', marginTop: '8px' }}>
                            Try adjusting your search filters or selecting a different destination.
                        </p>
                    </div>
                )}

                {/* Instructions */}
                {!selectedDestination && (
                    <div style={{
                        textAlign: 'center',
                        padding: '60px',
                        color: '#9ca3af'
                    }}>
                        <Search size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
                        <h3 style={{ color: '#d1d5db', marginBottom: '8px' }}>Welcome to Sid Check</h3>
                        <p>Select a destination country above to analyze all access origins for that destination.</p>
                        <p style={{ fontSize: '0.9rem', marginTop: '8px' }}>
                            This tool shows you which origins are accessing each specific destination.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SidCheck;
